import type { PersistedSnapshotV1, PersistedTableName } from "../persistence/records";
import { sha256Hex } from "./checksum";

export const BACKUP_FORMAT = "diet-tracker-backup" as const;
export const CURRENT_BACKUP_FORMAT_VERSION = 4 as const;
export const MAX_BACKUP_BYTES = 25 * 1024 * 1024;

export interface BackupEnvelopeV1 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 1;
  backupId: string;
  createdAt: string;
  appVersion: string;
  deviceId: string;
  revision: number;
  recordCounts: Record<string, number>;
  payload: unknown;
  checksumAlgorithm: "SHA-256";
  checksum: string;
}

export interface BackupEnvelopeV2 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 2;
  backupId: string;
  createdAt: string;
  appVersion: string;
  deviceId: string;
  revision: number;
  recordCounts: Record<string, number>;
  payload: unknown;
  checksumAlgorithm: "SHA-256";
  checksum: string;
}

export interface BackupEnvelopeV3 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 3;
  backupId: string;
  createdAt: string;
  appVersion: string;
  deviceId: string;
  revision: number;
  recordCounts: Record<PersistedTableName, number>;
  payload: PersistedSnapshotV1;
  checksumAlgorithm: "SHA-256";
  checksum: string;
}

export interface BackupEnvelopeV4 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 4;
  backupId: string;
  createdAt: string;
  appVersion: string;
  deviceId: string;
  revision: number;
  recordCounts: Record<PersistedTableName, number>;
  payload: PersistedSnapshotV1;
  checksumAlgorithm: "SHA-256";
  checksum: string;
}

export type CurrentBackupEnvelope = BackupEnvelopeV4;
export type BackupEnvelope = BackupEnvelopeV1 | BackupEnvelopeV2 | BackupEnvelopeV3 | BackupEnvelopeV4;

export interface BackupCreationOptions {
  backupId?: string;
  createdAt?: string;
  appVersion: string;
  deviceId: string;
  revision: number;
}

export async function createBackupEnvelope(
  payload: PersistedSnapshotV1,
  options: BackupCreationOptions,
): Promise<CurrentBackupEnvelope> {
  // Normalize structured-clone records to their actual JSON representation so
  // optional `undefined` properties cannot make in-memory and uploaded checksums differ.
  const normalizedPayload = JSON.parse(JSON.stringify(payload)) as PersistedSnapshotV1;
  const unsigned = {
    format: BACKUP_FORMAT,
    formatVersion: CURRENT_BACKUP_FORMAT_VERSION,
    backupId: options.backupId ?? createId(),
    createdAt: options.createdAt ?? new Date().toISOString(),
    appVersion: options.appVersion,
    deviceId: options.deviceId,
    revision: options.revision,
    recordCounts: recordCounts(normalizedPayload),
    payload: normalizedPayload,
    checksumAlgorithm: "SHA-256" as const,
  };

  return { ...unsigned, checksum: await sha256Hex(unsigned) };
}

export function serializeBackup(envelope: BackupEnvelope): string {
  return JSON.stringify(envelope);
}

export function recordCounts(payload: PersistedSnapshotV1): Record<PersistedTableName, number> {
  return {
    profiles: payload.profiles.length,
    settings: payload.settings.length,
    goalPhases: payload.goalPhases.length,
    nutrientGoals: payload.nutrientGoals.length,
    foods: payload.foods.length,
    recipes: payload.recipes.length,
    diaryEntries: payload.diaryEntries.length,
    weightEntries: payload.weightEntries.length,
    onboardingReferences: payload.onboardingReferences.length,
    weekPlans: payload.weekPlans.length,
    checkIns: payload.checkIns.length,
    backupState: payload.backupState.length,
    metadata: payload.metadata.length,
  };
}

function createId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `backup-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
